import WebApp from '@twa-dev/sdk';

export interface TelegramContext {
  initData: string;
  userId: number | null;
  username: string;
  displayName: string;
  isDark: boolean;
  colorScheme: 'light' | 'dark';
}

const WEB_CHAT_ID_KEY = 'mini_games_web_chat_id';

class TelegramService {
  private ctx: TelegramContext = {
    initData: '',
    userId: null,
    username: 'Player',
    displayName: 'Player',
    isDark: true,
    colorScheme: 'dark',
  };

  init(): TelegramContext {
    try {
      WebApp.ready();
      WebApp.expand();
      WebApp.setHeaderColor('#0a0a0f');
      WebApp.setBackgroundColor('#0a0a0f');

      const user = WebApp.initDataUnsafe?.user;
      this.ctx = {
        initData: WebApp.initData || '',
        userId: user?.id ?? null,
        username: user?.username ?? 'Player',
        displayName: user?.first_name ?? 'Player',
        isDark: WebApp.colorScheme === 'dark',
        colorScheme: WebApp.colorScheme,
      };

      if (!this.ctx.initData) {
        this.applyWebContext();
      }
    } catch {
      this.applyWebContext();
    }
    return this.ctx;
  }

  getContext(): TelegramContext {
    return this.ctx;
  }

  getInitData(): string {
    return this.ctx.initData;
  }

  getWebChatId(): number | null {
    return this.ctx.initData ? null : this.ctx.userId;
  }

  getAuthToken(): string {
    return this.ctx.initData || `web:${this.getOrCreateWebChatId()}`;
  }

  showMainButton(text: string, onClick: () => void): void {
    WebApp.MainButton.setText(text);
    WebApp.MainButton.show();
    WebApp.MainButton.onClick(onClick);
  }

  hideMainButton(): void {
    WebApp.MainButton.hide();
    WebApp.MainButton.offClick(() => {});
  }

  haptic(type: 'light' | 'medium' | 'heavy' = 'light'): void {
    try {
      if (type === 'light') WebApp.HapticFeedback.impactOccurred('light');
      else if (type === 'medium') WebApp.HapticFeedback.impactOccurred('medium');
      else WebApp.HapticFeedback.impactOccurred('heavy');
    } catch { /* noop */ }
  }

  close(): void {
    try { WebApp.close(); } catch { /* noop */ }
  }

  private applyWebContext(): void {
    const webChatId = this.getOrCreateWebChatId();
    this.ctx = {
      initData: '',
      userId: webChatId,
      username: `web_${webChatId}`,
      displayName: 'Web Player',
      isDark: true,
      colorScheme: 'dark',
    };
  }

  private getOrCreateWebChatId(): number {
    try {
      const existing = window.localStorage.getItem(WEB_CHAT_ID_KEY);
      if (existing && /^777\d+$/.test(existing)) {
        return Number(existing);
      }

      const next = this.createWebChatId();
      window.localStorage.setItem(WEB_CHAT_ID_KEY, String(next));
      return next;
    } catch {
      return this.createWebChatId();
    }
  }

  private createWebChatId(): number {
    const timePart = String(Date.now()).slice(-10);
    const randomPart = String(Math.floor(Math.random() * 1000)).padStart(3, '0');
    return Number(`777${timePart}${randomPart}`);
  }
}

export const telegram = new TelegramService();
